// Reine Rechnung des Homelab-Protokoll-Clients (docs/protokoll.md, „Phase 2").
// Ohne Svelte und ohne Browser, damit testbar (lib/protokoll-logik.test.mjs);
// client.svelte.ts hält nur die Leitung und ruft das hier.

export type Daten = Record<string, unknown>;
export type Rate = "live" | "normal" | "niedrig";

/** Stand einer Instanz auf Client-Seite. `wartet`: ein Snapshot ist
 *  angefordert, bis er da ist, werden Deltas verworfen. */
export interface Zustand {
  e: string | number;
  s: number;
  z: number;
  daten: Daten;
  wartet: boolean;
}

/** Meta, die jeder Handler mitbekommt. */
export interface Meta {
  e: string | number;
  s: number;
  z: number;
}

/** Delta mischen: Feld mit null → löschen, fehlt → bleibt. Liefert ein neues Objekt. */
export function mischeDelta(alt: Daten, delta: Daten): Daten {
  const neu: Daten = { ...alt };
  for (const k of Object.keys(delta)) {
    if (delta[k] === null) delete neu[k];
    else neu[k] = delta[k];
  }
  return neu;
}

/** Snapshot ersetzt alles. */
export function ausSnapshot(m: { e: string | number; s: number; z: number; d: Daten }): Zustand {
  return { e: m.e, s: m.s, z: m.z, daten: { ...(m.d ?? {}) }, wartet: false };
}

export interface DeltaFolge {
  neu: Zustand;
  /** true → dem Server `snapshot` für diese Instanz schicken. */
  snapshotAnfordern: boolean;
  /** true → `daten` haben sich geändert (leeres Delta = nur Lebenszeichen). */
  datenGeaendert: boolean;
}

/** Delta anwenden. `b !== s` → Delta verwerfen und genau einmal Snapshot
 *  anfordern; weitere Deltas verwirft man still, bis der Snapshot da ist.
 *  Die erste Nachricht einer Instanz je Verbindung trägt `e` (auch ein Delta
 *  nach Wiederaufnahme): weicht es vom gemerkten Stand ab, gilt der Stand
 *  als veraltet -- ebenfalls Snapshot anfordern. */
export function wendeDeltaAn(
  alt: Zustand,
  m: { b: number; s: number; z: number; d: Daten; e?: string | number },
): DeltaFolge {
  if (alt.wartet) return { neu: alt, snapshotAnfordern: false, datenGeaendert: false };
  if (m.b !== alt.s || (m.e !== undefined && m.e !== alt.e)) return { neu: { ...alt, wartet: true }, snapshotAnfordern: true, datenGeaendert: false };
  const d = m.d ?? {};
  const geaendert = Object.keys(d).length > 0;
  return {
    neu: { ...alt, s: m.s, z: m.z, daten: geaendert ? mischeDelta(alt.daten, d) : alt.daten },
    snapshotAnfordern: false,
    datenGeaendert: geaendert,
  };
}

/** Ereignis: ein schon gesehenes (gleiche Epoche, s nicht größer als der
 *  Stand) ist ein Duplikat nach der Wiederaufnahme und wird nicht noch
 *  einmal zugestellt -- bestätigt wird es trotzdem.
 *  Binär trägt nur die erste Nachricht je Instanz auf der Verbindung `e`:
 *  fehlt es, gilt die Epoche des bisherigen Standes (sie nie überschreiben,
 *  sonst schickt die Wiederaufnahme ein `e` ohne Wert). */
export function nimmEreignis(
  alt: Zustand | undefined,
  m: { e?: string | number; s: number; z: number },
): { neu: Zustand; zustellen: boolean } {
  const e = m.e ?? alt?.e ?? "";
  const neu: Zustand = { e, s: m.s, z: m.z, daten: {}, wartet: false };
  if (alt && alt.e === e && m.s <= alt.s) return { neu: alt, zustellen: false };
  return { neu, zustellen: true };
}

/** `luecke`: neuer Stand ab `s`. Auch hier fehlt `e`, wenn die Instanz auf
 *  dieser Verbindung schon vorgestellt war: die bisherige Epoche bleibt. */
export function nimmLuecke(alt: Zustand | undefined, m: { e?: string | number; s: number }): Zustand {
  return { e: m.e ?? alt?.e ?? "", s: m.s, z: 0, daten: {}, wartet: false };
}

/** Muster mit `*` je Segment gegen einen Instanznamen: `host.*.stats` passt
 *  auf `host.forge.stats`, nicht auf `host.stats` und nicht auf `host.a.b.stats`. */
export function passtMuster(muster: string, instanz: string): boolean {
  const m = muster.split(".");
  const i = instanz.split(".");
  if (m.length !== i.length) return false;
  return m.every((seg, n) => seg === "*" || seg === i[n]);
}

/** `stand` für ein `abo` nach dem Neuaufbau: alle bekannten Instanzen, die
 *  zum Muster passen, mit Epoche und letzter seq. */
export function standFuer(
  muster: string,
  instanzen: Iterable<[string, { e: string | number; s: number }]>,
): Record<string, { e: string | number; s: number }> {
  const aus: Record<string, { e: string | number; s: number }> = {};
  for (const [name, z] of instanzen) {
    if (passtMuster(muster, name)) aus[name] = { e: z.e, s: z.s };
  }
  return aus;
}

const RANG: Record<Rate, number> = { niedrig: 0, normal: 1, live: 2 };

/** Mehrere Abonnenten eines Musters: die höchste gewünschte Rate zählt;
 *  wünscht keiner eine, bleibt sie weg (Standard des Servers). */
export function hoechsteRate(raten: readonly (Rate | undefined)[]): Rate | undefined {
  let beste: Rate | undefined;
  for (const r of raten) {
    if (r && (!beste || RANG[r] > RANG[beste])) beste = r;
  }
  return beste;
}

/** Gebündelte Bestätigungen: je Instanz-ID zählt die höchste seq. Gesendet
 *  wird spätestens nach 1 s (Timer im Client) oder sofort, sobald `grenze`
 *  Ereignisse offen sind. */
export class AckPuffer {
  grenze: number;
  private offen = new Map<number, number>();
  private anzahl = 0;

  constructor(grenze = 50) {
    this.grenze = grenze;
  }

  /** Merkt ein erhaltenes Ereignis; true → jetzt senden. */
  merke(id: number, s: number): boolean {
    this.offen.set(id, Math.max(this.offen.get(id) ?? 0, s));
    this.anzahl++;
    return this.anzahl >= this.grenze;
  }

  get leer(): boolean {
    return this.offen.size === 0;
  }

  /** Alles Offene entnehmen (und leeren). */
  nimm(): { id: number; s: number }[] {
    const aus = [...this.offen].map(([id, s]) => ({ id, s }));
    this.offen.clear();
    this.anzahl = 0;
    return aus;
  }
}

const WARTE_MAX_MS = 30_000;

/** Wartezeit bis zum nächsten Verbindungsversuch: 1 s, 2 s, 4 s … 30 s mit
 *  Streuung -- wie ws-hub.svelte.ts (Safari-Energiebefund 01.09.2026). */
export function wartezeit(fehlversuche: number, zufall: () => number = Math.random): number {
  const roh = Math.min(1000 * 2 ** (Math.max(fehlversuche, 1) - 1), WARTE_MAX_MS);
  return roh + zufall() * Math.min(roh * 0.25, 3000);
}

export const NEULADEN_SPERRE_MS = 10 * 60_000;

/** Abweichende Schema-Version → einmal neu laden, aber höchstens alle
 *  10 min (gegen Schleifen, wenn Server und ausgelieferte App auseinanderliegen).
 *  `letzter`: der gemerkte Zeitpunkt des letzten Neuladens (sessionStorage). */
export function darfNeuladen(letzter: string | number | null | undefined, jetzt: number): boolean {
  const t = Number(letzter);
  if (letzter == null || letzter === "" || !Number.isFinite(t)) return true;
  return jetzt - t >= NEULADEN_SPERRE_MS;
}

/** Host-ID einer `host.<id>.stats`-Instanz: das mittlere Segment (bei Punkten
 *  in der ID alles zwischen erstem und letztem Segment). */
export function hostIdVon(instanz: string): string | undefined {
  const teile = instanz.split(".");
  if (teile.length < 3 || teile[0] !== "host" || teile[teile.length - 1] !== "stats") return undefined;
  return teile.slice(1, -1).join(".");
}

/** Zuordnung Kennung → Instanzname, gültig für EINE Verbindung (bei jedem
 *  Neuaufbau leeren). Die erste Nachricht je Instanz trägt `topic` -- auch ein
 *  `delta` nach Wiederaufnahme ohne Snapshot --, danach reicht die Kennung. */
export class IdKarte {
  private ids = new Map<number, string>();

  /** Setzt die Zuordnung, wenn `topic` da ist; liefert den Namen (oder undefined). */
  ordneZu(m: { id: number; topic?: unknown }): string | undefined {
    if (typeof m.topic === "string") {
      this.ids.set(m.id, m.topic);
      return m.topic;
    }
    return this.ids.get(m.id);
  }

  name(id: number): string | undefined {
    return this.ids.get(id);
  }

  vergiss(name: string): void {
    for (const [id, n] of this.ids) if (n === name) this.ids.delete(id);
  }

  leeren(): void {
    this.ids.clear();
  }
}

/** Was tun mit einem Delta, bevor es gemischt wird? Unbekannte Kennung ohne
 *  `topic` ist ein Nachzügler einer abbestellten Instanz (die erste Nachricht
 *  je Instanz trägt immer `topic`): still verwerfen, KEIN `snapshot` -- der
 *  Server zählt unnötige Anfragen als Fehler (20/min → Verbindung zu, 4003).
 *  Bekannter Name, aber kein eigener Stand: Snapshot anfordern. */
export function deltaWeg(name: string | undefined, hatStand: boolean): "verwerfen" | "snapshot" | "anwenden" {
  if (name === undefined) return "verwerfen";
  return hatStand ? "anwenden" : "snapshot";
}
